import { Box, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "@localization/useTranslation.ts";
import { DotLegalEmptyState, DotLegalLink, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import React, { ReactNode } from "react";
import NoSharings from "../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import DotLegalPaper from "../../../common/components/dotLegalPaper/DotLegalPaper.tsx";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { SystemSupplierTabTableViewModel } from "./SystemSuppliersTab.types.ts";
import { useSystemSuppliersTab } from "./SystemSuppliersTab.hooks.ts";

export interface SystemSuppliersTabProps {
    systemId: string;
}

function SystemSuppliersTab(props: SystemSuppliersTabProps) {
    const { translateString } = useTranslation();
    const { getContractOverviewUrl } = useUrlProvider();
    const {
        isLoading,
        hasData,
        rows,
        onAgreementClick,
        onNameClick,
        previewableDocuments,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useSystemSuppliersTab(props);

    function getTableHeader() {
        const headers = Array<ITableHeader<SystemSupplierTabTableViewModel>>();
        headers.push({ property: "name", text: translateString("supplier"), align: "left", showOnMobile: true });
        headers.push({ property: "typeString", text: translateString("type"), align: "left", showOnMobile: true });
        headers.push({ property: "countryString", text: translateString("country"), align: "left", showOnMobile: true });
        headers.push({ property: "agreements", text: translateString("agreement"), align: "left", showOnMobile: true });

        return headers;
    }

    function getAgreementsAndContractsCell(row: SystemSupplierTabTableViewModel) {
        const items: Array<ReactNode> = [];

        for (const agreement of row.agreements) {
            items.push(
                <Box
                    key={`agreement-${agreement.documentId}`}
                    component={"span"}
                    sx={(theme) => ({
                        cursor: "pointer",
                        color: theme.palette.primary.main,
                        fontWeight: theme.typography.fontWeightBold,
                        "&:hover": { textDecoration: "underline" },
                    })}
                    onClick={() => onAgreementClick(row, agreement)}
                >
                    {agreement.name}
                </Box>
            );
        }

        for (const contract of row.contracts) {
            items.push(
                <DotLegalLink bold key={`contract-${contract.id}`} to={getContractOverviewUrl(contract.id)} linkColor={"primary"}>
                    {contract.name}
                </DotLegalLink>
            );
        }

        if (items.length === 0) {
            if (row.isLink) {
                return "";
            }
            if (row.containsNotAccessibleDocuments || row.containsNotAccessibleContracts) {
                return translateString("yes");
            }
            return row.hasDataProcessorAgreement ? translateString("yes") : translateString("no");
        }

        return items.reduce<Array<ReactNode>>((acc, item, index) => {
            if (index > 0) {
                acc.push(<span key={`separator-${index}`}>, </span>);
            }
            acc.push(item);
            return acc;
        }, []);
    }

    return (
        <React.Fragment>
            <DotLegalPaper>
                {hasData ? (
                    <DotLegalTableWithFilterBar
                        data={rows}
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noSuppliersFound")}
                        rowsPerPage={30}
                        headers={getTableHeader()}
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={false}
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterOptions(),
                            fetchFilterData: true,
                            onMenuClose: onFilterChange,
                            onRemove: onFilterChange,
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        renderRow={(row, index) => (
                            <TableRow key={row.id + index}>
                                <TableCell>
                                    <Box sx={{ "& button": { font: "inherit", fontWeight: 700 } }}>
                                        <DotLegalLink onClick={() => onNameClick(row)} linkColor={"primary"}>
                                            {row.name}
                                        </DotLegalLink>
                                    </Box>
                                </TableCell>
                                <TableCell>{row.typeString}</TableCell>
                                <TableCell>{row.countryString}</TableCell>
                                <TableCell>{getAgreementsAndContractsCell(row)}</TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<NoSharings />} text={translateString("noSuppliersFound")} />
                )}
            </DotLegalPaper>
        </React.Fragment>
    );
}

export default SystemSuppliersTab;
